/**
 * Copyright (c) 2019-Present Syncari - All rights reserved.
 *
 * Design System — Stack layout primitive
 */
import cx from 'classnames';
import React from 'react';

import type { LayoutAlign, LayoutBreakpoint, LayoutJustify, LayoutSpacing } from '../../types';

import './Stack.less';

export interface StackProps {
  direction?: 'vertical' | 'horizontal';
  /**
   * Viewport breakpoint at which the direction switches to the opposite value.
   * Mobile-first: the `direction` prop applies *below* this breakpoint.
   * - vertical stacks become horizontal at this breakpoint and above
   * - horizontal stacks become vertical at this breakpoint and above
   */
  switchAt?: LayoutBreakpoint;
  gap?: LayoutSpacing;
  align?: LayoutAlign;
  justify?: LayoutJustify;
  fill?: boolean;
  scrollable?: boolean;
  divider?: boolean;
  className?: string;
  style?: React.CSSProperties;
  children?: React.ReactNode;
}

const Stack: React.FC<StackProps> = ({
  direction = 'vertical',
  switchAt,
  gap = 4,
  align,
  justify = 'start',
  fill = false,
  scrollable = false,
  divider = false,
  className,
  style,
  children,
}) => {
  const defaultAlign = direction === 'horizontal' ? 'center' : 'stretch';
  const resolvedAlign = align ?? defaultAlign;

  const classes = cx(
    'ds-stack',
    `ds-stack--${direction}`,
    !divider && gap !== undefined && `ds-stack--gap-${gap}`,
    `ds-stack--align-${resolvedAlign}`,
    `ds-stack--justify-${justify}`,
    fill && 'ds-stack--fill',
    scrollable && 'ds-stack--scrollable',
    divider && 'ds-stack--divider',
    switchAt && `ds-stack--switch-at-${switchAt}`,
    className
  );

  if (divider) {
    const items = React.Children.toArray(children).filter(Boolean);
    const withDividers: React.ReactNode[] = [];
    items.forEach((child, index) => {
      withDividers.push(child);
      if (index < items.length - 1) {
        withDividers.push(<hr key={`divider-${index}`} className="ds-stack__divider" aria-hidden="true" />);
      }
    });
    return (
      <div className={classes} style={style}>
        {withDividers}
      </div>
    );
  }

  return (
    <div className={classes} style={style}>
      {children}
    </div>
  );
};

Stack.displayName = 'Stack';

export { Stack };
export default Stack;
